import { Popover as RadixPopover } from 'radix-ui';
import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from 'react';

import { cn } from '@/utils/cn';

import { usePopoverContainer, type PopoverPortalTarget } from './popover-portal';

/**
 * The anchored floating panel every picker is built on.
 *
 * Direction is never chosen by a caller. The panel measures the space around its
 * trigger and flips, and when neither side fits it constrains its own height and
 * scrolls internally. A dialog that positions its own menus is a dialog that gets it
 * wrong on a laptop screen.
 */

export const Popover = RadixPopover.Root;
export const PopoverTrigger = RadixPopover.Trigger;
export const PopoverAnchor = RadixPopover.Anchor;

export interface PopoverContentProps
  extends ComponentPropsWithoutRef<typeof RadixPopover.Content> {
  popoverPortal?: PopoverPortalTarget;
  /**
   * When true (default), the panel is at least as wide as its trigger — correct for
   * listboxes and comboboxes. Calendars and other content-sized panels pass false so
   * a full-width trigger does not stretch an empty white band beside the grid.
   */
  matchTriggerWidth?: boolean;
}

export const PopoverContent = forwardRef<
  ElementRef<typeof RadixPopover.Content>,
  PopoverContentProps
>(
  (
    {
      className,
      align = 'start',
      sideOffset = 6,
      collisionPadding = 12,
      popoverPortal = 'body',
      matchTriggerWidth = true,
      style,
      ...props
    },
    ref,
  ) => {
    const { portal, container } = usePopoverContainer(popoverPortal);

    const content = (
      <RadixPopover.Content
        ref={ref}
        align={align}
        sideOffset={sideOffset}
        collisionPadding={collisionPadding}
        avoidCollisions
        className={cn(
          'z-50 rounded-lg border border-border bg-surface-raised p-1 text-fg shadow-e3',
          'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
          'data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
          className,
        )}
        style={{
          // Set by Radix from the measured collision box. Without maxHeight the
          // panel overflows the viewport; without minWidth a listbox renders
          // narrower than its trigger.
          maxHeight: 'var(--radix-popover-content-available-height)',
          ...(matchTriggerWidth
            ? { minWidth: 'var(--radix-popover-trigger-width)' }
            : undefined),
          ...style,
        }}
        {...props}
      />
    );

    if (!portal) return content;

    return (
      <RadixPopover.Portal container={container}>{content}</RadixPopover.Portal>
    );
  },
);

PopoverContent.displayName = 'PopoverContent';

export type { PopoverPortalTarget };
